import React from 'react';
import styled from 'styled-components';

import {
  useMutateReauthorizeDelegatedKey,
  useQueryWalletDelegatedKeyData,
} from '#/api/queries/delegatedKeysQueries';
import { useConfirmationModal } from '#/components/basic/modal/ConfirmationModal';
import { useSelector } from '#/reduxState/store';

import type { DelegatedKeyAuthorization } from '#/reduxState/delegatedKey/authorizationReducer';

const Wrapper = styled.div`
  line-height: 1.5;
  color: var(--gray4c);
`;

function ModalWrapper({
  currentDelegatedKey,
  delegatedKey,
}: {
  currentDelegatedKey: DelegatedKeyAuthorization;
  delegatedKey?: string;
}) {
  const { data } = useQueryWalletDelegatedKeyData(delegatedKey);

  return (
    <Wrapper>
      {data?.hasOpenOrders ? (
        <>
          {data.hasOpenOrders} order
          {data.hasOpenOrders > 1 && 's'} will be canceled.
          <p />
        </>
      ) : null}
      This renews the session key expiration. The same key address is kept.
      {currentDelegatedKey.walletAddress.toLowerCase() ===
        delegatedKey?.toLowerCase() && (
        <>
          <p />
          You will stay signed in with this session key.
        </>
      )}
    </Wrapper>
  );
}

export function useReauthorizeModal(
  currentDelegatedKey: DelegatedKeyAuthorization
) {
  const wallet = useSelector(state => state.wallet);
  const reauthorizeQuery = useMutateReauthorizeDelegatedKey();

  return useConfirmationModal<{
    delegatedKey?: string;
    delegatedName?: string;
  }>({
    confirmText: 'Reauthorize Session Key',
    title: 'Reauthorize Session Key',
    children: data => (
      <ModalWrapper
        delegatedKey={data?.delegatedKey}
        currentDelegatedKey={currentDelegatedKey}
      />
    ),
    confirmButtonProps: {
      autoFocus: true,
    },
    onConfirm: async ({ delegatedKey, delegatedName } = {}) => {
      if (!delegatedKey) {
        throw new Error('Failed to reauthorize Session key');
      }
      if (!wallet.walletType) {
        throw new Error('Unknown wallet type');
      }
      await reauthorizeQuery.mutateAsync({
        delegatedKey,
        delegatedName,
      });
    },
  });
}
